import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AccountDetail, Box, Button as ButtonUi } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { bool, func, number, shape, string } from "prop-types";
import moment from "moment";

import ContextMenu from "pages/_components/ContextMenu";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as layoutActions } from "reducers/layout";
import { actions as notificationActions } from "reducers/notification";
import { actions as productsActions } from "reducers/products";
import { actions as statusActions } from "reducers/status";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as settingsSelectors } from "reducers/settings";

import { accountContextMenuItems, getRutPathAccount, maskedAccountNumber, productTypes } from "util/accounts";
import { getIsoCurrency } from "util/deposits";
import * as config from "util/config";
import * as i18n from "util/i18n";
import { isMobileNative } from "util/device";
import isTokenActive from "util/token";
import Yup from "yup";
import {
    dispatchV3ShellLayout,
    isUxDashboardImprovementsEnabled,
    isUxMyAccountsImprovementsEnabled,
} from "util/v3ShellLayoutSync";
import { getBankAccountHistoricBalancePath, getBankAccountProjectedBalancePath } from "util/v3UxConfig";
import { getAccountDetailLabels } from "v3/v3UiKitI18n";
import { buildExtractMonthOptions, buildExtractYearOptions, getExtractDefaultYear } from "util/extractFilters";
import { getTransferUxI18nKey } from "util/transferInternalTitleLabels";
import {
    buildInitialMovementsFormValues,
    buildLatestMovementSearchByOptions,
    buildMovementOperationsOptions,
    buildPendingMovementSearchByOptions,
    dispatchFetchLatestMovements,
    dispatchDownloadLatestMovements,
    mapMovementToCardRow,
} from "v3/pages/accounts/accountDetailMovements";

function formatDocument(document, documentType) {
    const documentWithoutBlank = String(document)
        .replace(" ", "")
        .trim();
    if (documentType === "DNI" || documentWithoutBlank.length <= 2) {
        return documentWithoutBlank;
    }
    const documentFormated = `${documentWithoutBlank.substring(0, 2)}-`;
    if (documentWithoutBlank.length <= 10) {
        return documentFormated + documentWithoutBlank.substring(2);
    }
    return `${documentFormated}${documentWithoutBlank.substring(2, 10)}-${documentWithoutBlank.substring(10)}`;
}

function AccountDetailV3Page(props) {
    AccountDetailV3Page.propTypes = {
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        account: shape({}),
        fetching: bool.isRequired,
        fetchingDownload: bool.isRequired,
        hideAmount: bool.isRequired,
        activeEnvironment: shape({}),
        loggedUser: shape({}).isRequired,
        match: shape({}).isRequired,
        location: shape({}),
        latestMovements: shape([]).isRequired,
        pendingMovements: shape([]).isRequired,
        isFetchingMovements: bool.isRequired,
        isLatestMovementsSelected: bool.isRequired,
        latestMovementsPageNumber: number.isRequired,
        latestMovementsTotalPages: number.isRequired,
        pendingMovementsPageNumber: number.isRequired,
        pendingMovementsTotalPages: number.isRequired,
        listDateToYear: shape([]),
        fetchingAlias: bool.isRequired,
        tokenNumber: string,
        accountsHref: string,
    };

    AccountDetailV3Page.defaultProps = {
        account: null,
        activeEnvironment: null,
        location: undefined,
        listDateToYear: null,
        tokenNumber: null,
        accountsHref: "/v3/dashboard",
    };

    const {
        dispatch,
        isDesktop,
        account,
        fetching,
        fetchingDownload,
        hideAmount,
        activeEnvironment,
        loggedUser,
        match,
        location,
        latestMovements,
        pendingMovements,
        isFetchingMovements,
        isLatestMovementsSelected,
        latestMovementsPageNumber,
        latestMovementsTotalPages,
        pendingMovementsPageNumber,
        pendingMovementsTotalPages,
        listDateToYear,
        fetchingAlias,
        tokenNumber,
        accountsHref,
    } = props;

    const [showPartialAmounts, setShowPartialAmounts] = useState(false);
    const [remoteOpenModalAccountName, setRemoteOpenModalAccountName] = useState(false);
    const [lastMovementFilters, setLastMovementFilters] = useState(null);
    const initialLastMovementsValues = useMemo(() => buildInitialMovementsFormValues(getAccountDetailLabels), []);
    const initialPendingMovementsValues = useMemo(() => ({ ...initialLastMovementsValues, movementScope: "pending" }), [
        initialLastMovementsValues,
    ]);
    const latestMovementSearchByOptions = useMemo(() => buildLatestMovementSearchByOptions(getAccountDetailLabels), []);
    const pendingMovementSearchByOptions = useMemo(
        () => buildPendingMovementSearchByOptions(getAccountDetailLabels),
        [],
    );
    const movementOperationsOptions = useMemo(() => buildMovementOperationsOptions(getAccountDetailLabels), []);
    const enableHideAmounts = config.getBoolean("frontend.hide.accounts.balance.funcionalities", false);
    const hideAmounts = enableHideAmounts && hideAmount && !showPartialAmounts && !isDesktop;
    const useV3AccountEditModal = isUxMyAccountsImprovementsEnabled();
    const accountId = match?.params?.id;
    const goBackPath = useMemo(() => {
        if (accountsHref === "/desktop" && isUxDashboardImprovementsEnabled()) {
            return getRutPathAccount();
        }
        if (typeof accountsHref === "string" && accountsHref.startsWith("/")) {
            return accountsHref;
        }
        return getRutPathAccount();
    }, [accountsHref]);
    const currencyIso = account ? getIsoCurrency(String(account.currency)) : "ARS";

    const defaultSection = useMemo(() => {
        const tab = location?.state?.tabSelected;
        if (tab === 2) {
            return "information";
        }
        if (tab === 3) {
            return "extracts";
        }
        return "movements";
    }, [location?.state?.tabSelected]);

    useEffect(() => {
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
    }, [dispatch, isDesktop]);

    useEffect(() => {
        if (!accountId || !loggedUser?.userId) {
            return undefined;
        }
        dispatch(accountsActions.details(accountId, loggedUser.userId));
        dispatch(statusActions.saveLastHref(location));
        return undefined;
    }, [dispatch, accountId, loggedUser?.userId, location]);

    useEffect(() => {
        if (!account) {
            return undefined;
        }

        const activeSection =
            lastMovementFilters?.activeSection ?? (isLatestMovementsSelected ? "lastmovements" : "pendingmovements");
        const isLatest = activeSection !== "pendingmovements";

        dispatch(accountsActions.setIsLatestMovements(isLatest));

        const baseFilters =
            lastMovementFilters ?? (isLatest ? initialLastMovementsValues : initialPendingMovementsValues);

        dispatchFetchLatestMovements(dispatch, accountsActions, {
            account,
            isDesktop,
            latestMovementsPageNumber: "1",
            pendingMovementsPageNumber: "1",
            formValues: { ...baseFilters, activeSection },
        });
        return () => {
            dispatch(accountsActions.removeMovements());
            dispatch(accountsActions.cleanAccountSummaries());
        };
    }, [dispatch, account?.idProduct, isDesktop]);

    const handleBack = () => dispatch(push(goBackPath));

    const handleProjectedBalance = () => {
        if (!account) {
            return;
        }
        dispatch(push(getBankAccountProjectedBalancePath(account.idProduct)));
        dispatch(accountsActions.proyectedBalance(account.idProduct));
        dispatch(accountsActions.diferedMovements(account.idProduct, "1"));
    };

    const handleHistoricBalance = () => {
        if (!account) {
            return;
        }
        dispatch(push(getBankAccountHistoricBalancePath(account.idProduct)));
    };

    const handleDownloadInfo = (format) => {
        if (!account) {
            return;
        }
        const { productType, number: accountNumber, cbu, cbuAlias, ownerName, documentNumber, currency } = account;
        const accountType = i18n.get(`accounts.productType.${productTypes[productType]}`).toUpperCase();
        const currencyType = i18n.get(`currency.label.${currency}`);
        const accountLabel = `${accountType} en ${currencyType}`;
        const cuit = documentNumber ? formatDocument(documentNumber, "CUIT") : " - ";
        dispatch(
            accountsActions.downloadInformation(accountId, {
                accountLabel,
                accountNumber,
                cbu,
                cbuAlias,
                ownerName,
                cuit,
                format,
            }),
        );
    };

    const handleMovementsSubmit = useCallback(
        (formValues) => {
            if (!account) {
                return;
            }
            setLastMovementFilters(formValues);
            const isLatest = formValues.activeSection !== "pendingmovements";
            dispatch(accountsActions.setIsLatestMovements(isLatest));
            dispatchFetchLatestMovements(dispatch, accountsActions, {
                account,
                isDesktop,
                formValues,
            });
        },
        [account, dispatch, isDesktop],
    );

    const handleMovementsExport = useCallback(
        (format) => {
            if (!account) {
                return;
            }
            const activeSection = isLatestMovementsSelected ? "lastmovements" : "pendingmovements";
            const baseFilters =
                lastMovementFilters ??
                (activeSection === "lastmovements" ? initialLastMovementsValues : initialPendingMovementsValues);
            dispatchDownloadLatestMovements(dispatch, accountsActions, {
                account,
                format,
                isLatestMovementsSelected,
                latestMovementsPageNumber,
                pendingMovementsPageNumber,
                formValues: { ...baseFilters, activeSection },
            });
        },
        [
            account,
            dispatch,
            initialLastMovementsValues,
            initialPendingMovementsValues,
            isLatestMovementsSelected,
            lastMovementFilters,
            latestMovementsPageNumber,
            pendingMovementsPageNumber,
        ],
    );

    const handleMovementsNextPage = useCallback(
        (nextPage) => {
            if (!account) {
                return;
            }
            const activeSection = isLatestMovementsSelected ? "lastmovements" : "pendingmovements";
            const baseFilters =
                lastMovementFilters ??
                (activeSection === "lastmovements" ? initialLastMovementsValues : initialPendingMovementsValues);
            dispatchFetchLatestMovements(dispatch, accountsActions, {
                account,
                isDesktop,
                latestMovementsPageNumber: isLatestMovementsSelected ? nextPage : latestMovementsPageNumber,
                pendingMovementsPageNumber: !isLatestMovementsSelected ? nextPage : pendingMovementsPageNumber,
                formValues: { ...baseFilters, activeSection },
            });
        },
        [
            account,
            dispatch,
            initialLastMovementsValues,
            initialPendingMovementsValues,
            isDesktop,
            isLatestMovementsSelected,
            lastMovementFilters,
            latestMovementsPageNumber,
            pendingMovementsPageNumber,
        ],
    );

    const menuActions = useMemo(() => {
        if (!account || !activeEnvironment) {
            return [];
        }
        const items = accountContextMenuItems(dispatch, activeEnvironment, account.idProduct, [], location?.pathname);
        return items.map((item) => ({
            icon: item.icon || "CashflowIn",
            iconVariant: "outline",
            label: i18n.get(
                item.label === "accounts.actions.transfer.internal" ? getTransferUxI18nKey(item.label) : item.label,
            ),
            callback: item.onClick,
        }));
    }, [account, activeEnvironment, dispatch, location?.pathname]);

    const informationConfig = useMemo(() => {
        if (!account || !activeEnvironment) {
            return { data: [] };
        }
        const {
            productType,
            currency,
            number: accountNumber,
            cbu,
            cbuAlias,
            ownerName,
            documentNumber,
            documentType,
            shortLabel,
        } = account;
        const accountType = i18n.get(`accounts.productType.${productTypes[productType]}`).toUpperCase();
        const currencyType = i18n.get(`currency.label.${currency}`);
        const accountLabel = `${accountType} en ${currencyType}`;
        const onCopied = () =>
            dispatch(
                notificationActions.showNotification(i18n.get("accounts.information.data.copied"), "success", [
                    "account/details",
                ]),
            );

        return {
            data: [
                { title: i18n.get("accounts.information.accountType"), value: accountLabel || " - " },
                { title: i18n.get("accounts.information.accountNumber"), value: accountNumber || " - " },
                {
                    title: i18n.get("accounts.information.cbu").toUpperCase(),
                    value: cbu || " - ",
                    canCopy: true,
                    canShare: isMobileNative,
                    copyText: cbu,
                    onCopy: onCopied,
                },
                {
                    title: i18n.get("accounts.information.alias"),
                    value: cbuAlias,
                    canCopy: true,
                    canShare: isMobileNative,
                    copyText: cbuAlias,
                    onCopy: onCopied,
                    tooltipText: i18n.get("accounts.cbuAlias.edit.info"),
                    tooltipPlacement: "right",
                },
                { title: i18n.get("accounts.information.headlines"), value: ownerName || " - " },
                {
                    title: i18n.get(`documentType.label.${documentType}`),
                    value: documentNumber ? formatDocument(documentNumber, documentType) : " - ",
                },
            ],
            isV3Layout: true,
            showShareButton: isMobileNative,
            onShare: () =>
                dispatch(
                    accountsActions.shareAccount({
                        header: activeEnvironment.name,
                        cbu,
                        accountType: shortLabel.substring(shortLabel.indexOf(productType)),
                        alias: cbuAlias || "",
                        cuitCuil: activeEnvironment.clients[0].idClient,
                        footer: "global.companyName",
                    }),
                ),
            onDownload: handleDownloadInfo,
            downloadLoading: fetchingDownload,
        };
    }, [account, activeEnvironment, dispatch, fetchingDownload]);

    const movementRows = useMemo(() => {
        const source = isLatestMovementsSelected ? latestMovements : pendingMovements;
        return (source || []).map((movement) => mapMovementToCardRow(movement, currencyIso, hideAmounts));
    }, [currencyIso, hideAmounts, isLatestMovementsSelected, latestMovements, pendingMovements]);

    const movementsFilterConfig = useMemo(
        () => ({
            currency: currencyIso,
            getLabels: getAccountDetailLabels,
            onSubmit: handleMovementsSubmit,
            searchByOptions: latestMovementSearchByOptions,
            pendingSearchByOptions: pendingMovementSearchByOptions,
            operationsOptions: movementOperationsOptions,
            initialLastMovementsValues,
            initialPendingMovementsValues,
            movements: movementRows,
            movementsLoading: isFetchingMovements,
            movementsHideAmounts: hideAmounts,
            movementsEmptyStateText: getAccountDetailLabels("empty"),
            movementsEndMessage: i18n.get("transactions.list.noMoreTransactions"),
            onExportMovements: handleMovementsExport,
            movementsExportLoading: fetchingDownload,
            movementsPaginationProps:
                movementRows.length > 0
                    ? {
                          action: handleMovementsNextPage,
                          pageNumber: isLatestMovementsSelected
                              ? latestMovementsPageNumber
                              : pendingMovementsPageNumber,
                          totalPages: isLatestMovementsSelected
                              ? latestMovementsTotalPages
                              : pendingMovementsTotalPages,
                      }
                    : undefined,
        }),
        [
            currencyIso,
            fetchingDownload,
            handleMovementsExport,
            handleMovementsNextPage,
            handleMovementsSubmit,
            hideAmounts,
            initialLastMovementsValues,
            initialPendingMovementsValues,
            isFetchingMovements,
            isLatestMovementsSelected,
            latestMovementSearchByOptions,
            latestMovementsPageNumber,
            latestMovementsTotalPages,
            movementOperationsOptions,
            movementRows,
            pendingMovementSearchByOptions,
            pendingMovementsPageNumber,
            pendingMovementsTotalPages,
        ],
    );

    const extractsConfig = useMemo(() => {
        if (!account) {
            return undefined;
        }
        const defaultYear = getExtractDefaultYear();
        const yearOptions = buildExtractYearOptions();
        const extractRows = (listDateToYear || []).map((item) => ({
            id: item.extractId,
            label: `${i18n.get("accounts.summaries.listDateToYear")} ${moment(item.fechaHasta).format("DD/MM/YYYY")}`,
        }));
        const allMonthsLabel = i18n.get("channels.all");
        const initialValues = {
            year: { value: String(defaultYear), label: String(defaultYear) },
            month: { value: "all", label: allMonthsLabel },
        };

        return {
            getLabels: getAccountDetailLabels,
            yearOptions,
            getMonthOptionsForYear: buildExtractMonthOptions,
            initialValues,
            onSubmit: (values) => {
                const year = Number(values.year?.value ?? values.year);
                const monthValue = values.month?.value;
                const month = monthValue === "all" || monthValue == null ? allMonthsLabel : monthValue;
                dispatch(accountsActions.summariesQuery(account.idProduct, year, month));
            },
            onClearFilters: () => dispatch(accountsActions.cleanAccountSummaries()),
            onExtractDownload: (extractId) =>
                dispatch(accountsActions.summariesExtract(extractId, account.productType)),
            list: {
                array: extractRows,
                loading: isFetchingMovements,
                emptyStateText: i18n.get("accounts.summaries.noMoreMovements"),
            },
        };
    }, [account, dispatch, isFetchingMovements, listDateToYear]);

    const handleEditAccountNameFromMenu = useCallback(() => {
        if (!account) {
            return;
        }
        if (useV3AccountEditModal) {
            setRemoteOpenModalAccountName(true);
            return;
        }
        dispatch(push(`/account/${account.idProduct}/setProductAlias`));
    }, [account, dispatch, useV3AccountEditModal]);

    const editAccountNameValidationSchema = useMemo(
        () =>
            Yup.object({
                accountName: Yup.string()
                    .trim()
                    .required(i18n.get("accounts.v3.detail.form.account.name.required"))
                    .max(50, i18n.get("accounts.v3.detail.form.account.name.maxLength")),
            }),
        [],
    );

    const validateEditAccountAlias = useCallback((aliasValue) => {
        const trimmed = aliasValue?.trim() ?? "";
        if (!trimmed) {
            return i18n.get("accounts.cbuAlias.setAlias.empty");
        }
        if (trimmed.length > 20) {
            return i18n.get("accounts.v3.detail.form.account.alias.maxLength");
        }
        return undefined;
    }, []);

    const headerTitle = account ? account.productAlias || maskedAccountNumber(account.number) : "";
    const headerSubtitle = account
        ? `${i18n.get(`accounts.productType.${productTypes[account.productType]}`)} Nº ${maskedAccountNumber(
              account.number,
          )}`
        : "";

    const mobileShellRight = useMemo(() => {
        if (!account || !activeEnvironment) {
            return null;
        }
        const dropdownItems = [
            {
                label: "products.alias.account.edit.title.mobile",
                onClick: handleEditAccountNameFromMenu,
                icon: "Pencil",
            },
            {
                label: "accounts.projected.balance",
                onClick: handleProjectedBalance,
                icon: "StocksUp",
            },
            {
                label: "accounts.historic.balance",
                onClick: handleHistoricBalance,
                icon: "MemoMoney",
            },
        ];
        return (
            <Box flex align="center" gap={12}>
                {enableHideAmounts && hideAmount && (
                    <ButtonUi
                        variant="text"
                        type="button"
                        aria-label="Mostrar saldos durante 3 segundos"
                        icon={!showPartialAmounts ? "EyeOff" : "Eye"}
                        iconVariant="outline"
                        color="white"
                        size="large"
                        css={{ padding: 8 }}
                        onClick={() => {
                            setShowPartialAmounts(true);
                            window.setTimeout(() => setShowPartialAmounts(false), 3000);
                        }}
                    />
                )}
                <ContextMenu
                    isDesktop={false}
                    color="white"
                    items={accountContextMenuItems(
                        dispatch,
                        activeEnvironment,
                        account.idProduct,
                        dropdownItems,
                        location?.pathname,
                    )}
                />
            </Box>
        );
    }, [
        account,
        activeEnvironment,
        dispatch,
        enableHideAmounts,
        handleEditAccountNameFromMenu,
        hideAmount,
        location?.pathname,
        showPartialAmounts,
    ]);

    useEffect(() => {
        if (!isUxDashboardImprovementsEnabled() || fetching || !account) {
            return undefined;
        }
        if (!isDesktop) {
            dispatchV3ShellLayout(dispatch, {
                headerTitle,
                headerSubtitle,
                goBackPath,
                scopes: ["account/details", "account/alias"],
                rightContentMobile: mobileShellRight,
            });
        } else {
            dispatch(layoutActions.setScopes(["account/details", "account/alias"]));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        }
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [dispatch, isDesktop, fetching, account, headerTitle, headerSubtitle, mobileShellRight, goBackPath]);

    const submitEditAccountName = useCallback(
        async (arg) => {
            if (!account) {
                return;
            }
            const name = typeof arg === "string" ? arg : arg?.accountName;
            if (!name) {
                return;
            }
            dispatch(productsActions.changeProductAlias(name, account.idProduct));
        },
        [account, dispatch],
    );

    const submitEditAccountAlias = useCallback(
        (newAlias, otp) =>
            new Promise((resolve, reject) => {
                if (!account) {
                    reject(new Error("No account"));
                    return;
                }
                const trimmedAlias = newAlias?.trim();
                if (!trimmedAlias) {
                    reject(new Error("Alias required"));
                    return;
                }
                const finalOtp = !isDesktop && isTokenActive() && tokenNumber ? tokenNumber : otp;
                if (!finalOtp) {
                    reject(new Error("OTP required"));
                    return;
                }
                const { documentNumber: cuit, cbu, cbuAlias, idProduct: idAccount } = account;
                dispatch(
                    accountsActions.modifyCbuAlias(
                        idAccount,
                        cuit,
                        cbu,
                        cbuAlias,
                        trimmedAlias,
                        { otp: finalOtp },
                        null,
                        resolve,
                        reject,
                    ),
                );
            }),
        [account, dispatch, isDesktop, tokenNumber],
    );

    const aliasRules = useMemo(
        () => [
            i18n.get("accounts.cbuAlias.setAlias.v3.info1"),
            i18n.get("accounts.cbuAlias.setAlias.v3.info2"),
            i18n.get("accounts.cbuAlias.setAlias.v3.info3"),
            i18n.get("accounts.cbuAlias.setAlias.v3.info4"),
        ],
        [],
    );

    const getAliasEditToken = useCallback(async () => {
        if (!isDesktop && isTokenActive() && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    }, [isDesktop, tokenNumber]);

    const aliasOtpSchema = useMemo(
        () =>
            Yup.object({
                otp:
                    !isDesktop && isTokenActive() && tokenNumber
                        ? Yup.string().nullable()
                        : Yup.string().required(i18n.get("token.required")),
            }),
        [isDesktop, tokenNumber],
    );

    const aliasConfirmationProps = useMemo(
        () => ({
            getToken: getAliasEditToken,
            validationConfirm: aliasOtpSchema,
            confirmationText: i18n.get("form.credential.hint"),
            handleCatchError: () => {},
        }),
        [getAliasEditToken, aliasOtpSchema],
    );

    return account || fetching ? (
        <AccountDetail
            loading={fetching}
            title={headerTitle}
            subtitle={headerSubtitle}
            goBack={handleBack}
            isOperative={Boolean(account?.favorite)}
            onProjectedBalance={handleProjectedBalance}
            onHistoricBalance={handleHistoricBalance}
            projectedBalanceLabel={i18n.get("accounts.projected.balance")}
            historicBalanceLabel={i18n.get("accounts.historic.balance")}
            menuActions={menuActions}
            currency={currencyIso}
            hideAmounts={hideAmounts}
            availableBalance={account?.balance}
            realBalance={account?.countableBalance}
            initialBalance={account?.saldoApertura}
            agreements={account?.acuerdos}
            availableBalanceLabel={getAccountDetailLabels("availableBalance")}
            realBalanceLabel={getAccountDetailLabels("realBalance")}
            initialBalanceLabel={i18n.get("accounts.aperturaBalance")}
            agreementsLabel={i18n.get("accounts.acuerdosBalance")}
            showBalanceChartLabel={getAccountDetailLabels("showBalanceChart")}
            hideBalanceChartLabel={getAccountDetailLabels("hideBalanceChart")}
            getLabels={getAccountDetailLabels}
            defaultSection={defaultSection}
            movementsFilterConfig={movementsFilterConfig}
            informationConfig={informationConfig}
            extractsConfig={extractsConfig}
            alias={account?.cbuAlias}
            aliasRules={aliasRules}
            aliasConfirmationProps={aliasConfirmationProps}
            submitLoading={fetchingAlias}
            submitEditAccountName={submitEditAccountName}
            submitEditAccountAlias={submitEditAccountAlias}
            editAccountNameValidationSchema={editAccountNameValidationSchema}
            validateEditAccountAlias={validateEditAccountAlias}
            remoteOpenModalAccountName={remoteOpenModalAccountName}
            onRemoteOpenModalAccountNameHandled={() => setRemoteOpenModalAccountName(false)}
        />
    ) : null;
}

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    account: accountsSelectors.getSelectedAccount(state),
    fetching: accountsSelectors.getFetching(state),
    fetchingDownload: accountsSelectors.getFetchingDownload(state),
    loggedUser: sessionSelectors.getUser(state),
    hideAmount: Boolean(settingsSelectors.getUserData(state)?.hideBalance),
    latestMovements: accountsSelectors.getLatestMovements(state),
    pendingMovements: accountsSelectors.getPendingMovements(state),
    isFetchingMovements: accountsSelectors.getFetchingMovements(state),
    isLatestMovementsSelected: accountsSelectors.isLatestMovementsSelected(state),
    latestMovementsPageNumber: accountsSelectors.getLatestMovementsPageNumber(state),
    latestMovementsTotalPages: accountsSelectors.getLatestMovementsTotalPages(state),
    pendingMovementsPageNumber: accountsSelectors.getPendingMovementsPageNumber(state),
    pendingMovementsTotalPages: accountsSelectors.getPendingMovementsTotalPages(state),
    listDateToYear: accountsSelectors.getListDateToYear(state),
    fetchingAlias: accountsSelectors.getFetchingAlias(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
    accountsHref: accountsSelectors.getAccountsLastHref(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AccountDetailV3Page);
